import React, { useState, useRef } from "react";
import "./DriverForm.css";
import { createDriverOffer } from "../services/firebaseApi";
import { watchAllRequestsForCounts } from "../services/requests";
import { getNextMeetings, getNextMeetingsAsync, onMeetingsCacheUpdate } from "../utils/dateHelpers";
import NameAutocomplete from "./NameAutocomplete";
import AddressAutocomplete from "./AddressAutocomplete";
import { upsertProfile, getByNameExact } from "../services/profileStore";
import { addMyRequest } from "../services/myRequests";

const DriverForm = ({ onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    driverName: "",
    departureAddress: "",
    city: "",
    postalCode: "",
    availableSeats: 1,
    meetingAt: null,
    isoDate: "",
    routine: false,
    selectedMeetings: [], // [{meetingAt, isoDate}]
    icon: undefined, // emoji véhicule facultatif
  });
  const [submitting, setSubmitting] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({}); // { isoDate: number }
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [upcomingMeetings, setUpcomingMeetings] = useState(() => getNextMeetings(8));

  // Charger les réunions depuis Firebase (avec dates exclues)
  React.useEffect(() => {
    const loadMeetings = async () => {
      try {
        const meetings = await getNextMeetingsAsync(8);
        setUpcomingMeetings(meetings);
      } catch (error) {
        console.warn('⚠️ [DriverForm] Erreur chargement async meetings:', error);
        // En cas d'erreur, garder les réunions synchrones
      }
    };
    
    loadMeetings();
    
    // Écouter les mises à jour du cache réactif
    const unsubscribe = onMeetingsCacheUpdate(async () => {
      console.log('🔄 [DriverForm] Cache mis à jour, rechargement...');
      await loadMeetings();
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Compter les demandes non acceptées par date (pour badges rouges sur les options)
  React.useEffect(() => {
    try {
      const unsubscribe = watchAllRequestsForCounts((rows) => {
        const counts = {};
        (rows || []).forEach((r) => {
          const iso = r?.isoDate || (r?.meetingAt ? new Date(r.meetingAt).toISOString().split("T")[0] : null);
          if (!iso) return;
          counts[iso] = (counts[iso] || 0) + 1;
        });
        setPendingCounts(counts);
      });
      return () => { try { unsubscribe && unsubscribe(); } catch {} };
    } catch (e) {
      console.warn("⚠️ Impossible de compter les demandes en attente", e);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectProfile = async (p) => {
    // Pré-remplir les champs de base
    setFormData((prev) => ({
      ...prev,
      driverName: p.name || prev.driverName,
      icon: p.icon || prev.icon,
    }));

    // Si on a une ville et un code postal mais pas d'adresse complète, essayer de compléter
    if (p.city && p.postalCode && (!p.address || p.address.trim() === "")) {
      // Essayer de trouver une adresse complète via l'API
      try {
        const { searchAddresses } = await import("../services/addressAutocomplete");
        const query = `${p.city} ${p.postalCode}`;
        const suggestions = await searchAddresses(query, 1);
        
        if (suggestions && suggestions.length > 0) {
          const bestMatch = suggestions[0];
          setFormData((prev) => ({
            ...prev,
            departureAddress: bestMatch.street || bestMatch.fullAddress || prev.departureAddress,
            city: bestMatch.city || p.city || prev.city,
            postalCode: bestMatch.postcode || p.postalCode || prev.postalCode,
          }));
        } else {
          // Si pas de suggestion, utiliser au moins ce qu'on a
          setFormData((prev) => ({
            ...prev,
            city: p.city || prev.city,
            postalCode: p.postalCode || prev.postalCode,
          }));
        }
      } catch (error) {
        console.warn("Erreur lors de la complétion d'adresse:", error);
        // En cas d'erreur, utiliser au moins ce qu'on a
        setFormData((prev) => ({
          ...prev,
          city: p.city || prev.city,
          postalCode: p.postalCode || prev.postalCode,
        }));
      }
    } else {
      // Si on a déjà une adresse complète, l'utiliser directement
      setFormData((prev) => ({
        ...prev,
        departureAddress: p.address != null && p.address !== "" ? p.address : prev.departureAddress,
        city: p.city != null && p.city !== "" ? p.city : prev.city,
        postalCode:
          p.postalCode != null && p.postalCode !== ""
            ? p.postalCode
            : prev.postalCode,
      }));
    }
  };

  const handleAddressChange = (addressData) => {
    console.log("📥 DriverForm reçoit:", addressData);

    const newData = {
      departureAddress: addressData.address || "",
      city: addressData.city || "",
      postalCode: addressData.postcode || "",
    };

    console.log("📝 Mise à jour avec:", newData);

    setFormData((prev) => {
      const updated = {
        ...prev,
        ...newData,
      };
      
      console.log("🔄 formData mis à jour:", updated);
      return updated;
    });
  };

  const handleLocationFound = (locationData) => {
    console.log("📍 handleLocationFound appelé avec:", locationData);
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        departureAddress: locationData.address || "",
        city: locationData.city || "",
        postalCode: locationData.postalCode || "",
      };
      console.log(
        "📝 handleLocationFound: formData mis à jour:",
        updatedFormData,
      );
      return updatedFormData;
    });
  };

  const toggleMeetingSelection = (meeting) => {
    setFormData((prev) => {
      const exists = prev.selectedMeetings.some(
        (m) => m.meetingAt === meeting.meetingAt,
      );
      const next = exists
        ? prev.selectedMeetings.filter((m) => m.meetingAt !== meeting.meetingAt)
        : [
            ...prev.selectedMeetings,
            { meetingAt: meeting.meetingAt, isoDate: meeting.isoDate },
          ];
      return { ...prev, selectedMeetings: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    if (
      !formData.driverName ||
      !formData.departureAddress ||
      !formData.city ||
      !formData.postalCode ||
      !formData.availableSeats
    ) {
      alert(
        "Veuillez remplir tous les champs (prénom, adresse de départ, ville, code postal, places)",
      );
      setSubmitting(false);
      return;
    }

    if (!formData.routine) {
      const count =
        formData.selectedMeetings.length || (formData.meetingAt ? 1 : 0);
      if (count === 0) {
        alert("Veuillez sélectionner au moins une date");
        setSubmitting(false);
        return;
      }
    }

    try {
      let created = 0;
      if (formData.routine) {
        const list = upcomingMeetings;
        for (const m of list) {
          const result = await createDriverOffer({
            driverName: formData.driverName,
            startAddress: formData.departureAddress,
            city: formData.city,
            postalCode: formData.postalCode,
            seats: Number(formData.availableSeats),
            meetingAt: m.meetingAt,
            isoDate: m.isoDate,
            icon: formData.icon,
          });

          // Sauvegarder l'ID de l'offre créée
          if (result && result.key) {
            addMyRequest(result.key);
          }

          created += 1;
        }
      } else {
        const list =
          formData.selectedMeetings.length > 0
            ? formData.selectedMeetings
            : [{ meetingAt: formData.meetingAt, isoDate: formData.isoDate }];
        for (const m of list) {
          const result = await createDriverOffer({
            driverName: formData.driverName,
            startAddress: formData.departureAddress,
            city: formData.city,
            postalCode: formData.postalCode,
            seats: Number(formData.availableSeats),
            meetingAt: m.meetingAt,
            isoDate: m.isoDate,
            icon: formData.icon,
          });

          // Sauvegarder l'ID de l'offre créée
          if (result && result.key) {
            addMyRequest(result.key);
          }

          created += 1;
        }
      }

      // Sauvegarder le profil utilisateur avec icône
      try {
        await upsertProfile({
          name: formData.driverName,
          city: formData.city,
          postalCode: formData.postalCode,
          icon: formData.icon || undefined,
        });
        localStorage.setItem("cvtrg_current_user", formData.driverName);
      } catch {}

      onSubmit && onSubmit();
      alert(created > 1 ? `${created} offres créées.` : "Offre créée.");
    } catch (err) {
      if (err.message === "DUPLICATE_DRIVER_OFFER") {
        alert(
          "Une offre identique existe déjà pour cette date et cette adresse.",
        );
      } else {
        alert("Erreur lors de la création de votre proposition");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="driver-form">
      <div className="form-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <h2>🚗 Proposer ma voiture</h2>
      </div>

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="driverName">Votre prénom :</label>
          <div style={{ position:'relative' }}>
            <NameAutocomplete
              value={formData.driverName}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, driverName: val }))
              }
              onSelectProfile={handleSelectProfile}
              placeholder="Entrez votre prénom"
            />
            <button
              type="button"
              onClick={() => setShowIconPicker(v => !v)}
              aria-label="Choisir une icône véhicule"
              style={{
                position:'absolute', right:8, top:8, width:34, height:34,
                borderRadius:8, border:'1px solid #E5E7EB', background:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 1px 2px rgba(0,0,0,.06)', cursor:'pointer',
                fontSize:20
              }}
            >
              {formData.icon || '🚗'}
            </button>
          </div>
        </div>

        {showIconPicker && (
          <div className="form-group">
            <label>Icône véhicule (facultatif) :</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {['', '🚗','🚙','🚕','🛻','🚐','🚚','🏎️','🚘'].map((emo) => (
                <button
                  key={emo || 'none'}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, icon: emo || undefined }))}
                  aria-label={emo ? `Choisir ${emo}` : 'Aucune icône'}
                  style={{
                    width:36, height:36, borderRadius:8,
                    border: formData.icon === emo ? '2px solid #7c3aed' : '1px solid #d1d5db',
                    background:'#fff', cursor:'pointer', fontSize:20
                  }}
                >
                  {emo || '—'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Adresse de départ :</label>
          <AddressAutocomplete
            showCarpoolButton={false}
            value={{
              address: formData.departureAddress,
              postcode: formData.postalCode,
              city: formData.city,
            }}
            onChange={handleAddressChange}
            placeholder="Ex: 3 rue des Écoles"
            showValidation={true}
          />

          {/* Bouton de géolocalisation intégré */}
          <button
            type="button"
            onClick={async () => {
              if (!navigator.geolocation) {
                alert(
                  "La géolocalisation n'est pas supportée par votre navigateur",
                );
                return;
              }

              try {
                const position = await new Promise((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000,
                  });
                });

                const { latitude, longitude } = position.coords;

                console.log("📍 Géolocalisation: Coordonnées GPS récupérées:", {
                  latitude,
                  longitude,
                });

                try {
                  // Utiliser l'API pour convertir les coordonnées en adresse
                  const response = await fetch(
                    `https://api-adresse.data.gouv.fr/reverse/?lon=${longitude}&lat=${latitude}`,
                  );
                  const data = await response.json();

                  console.log("🌐 API Reverse Geocoding réponse:", data);

                  if (data.features && data.features.length > 0) {
                    const feature = data.features[0];
                    const properties = feature.properties;

                    const realAddress = {
                      address:
                        properties.name ||
                        `${properties.housenumber || ""} ${properties.street || ""}`.trim(),
                      postcode: properties.postcode || "",
                      city: properties.city || "",
                    };

                    console.log("🏠 Adresse réelle trouvée:", realAddress);

                    setFormData((prev) => {
                      const updatedFormData = {
                        ...prev,
                        departureAddress: realAddress.address,
                        city: realAddress.city,
                        postalCode: realAddress.postcode,
                      };
                      console.log(
                        "📝 Géolocalisation: formData mis à jour avec vraie adresse:",
                        updatedFormData,
                      );
                      return updatedFormData;
                    });

                    alert(
                      "Localisation réussie ! Votre vraie adresse a été trouvée et remplie.",
                    );
                  } else {
                    // Fallback si l'API ne trouve rien
                    const fallbackAddress = {
                      address: "Adresse non trouvée",
                      postcode: "41000",
                      city: "Blois",
                    };

                    console.log(
                      "⚠️ Fallback: Adresse factice utilisée:",
                      fallbackAddress,
                    );

                    setFormData((prev) => {
                      const updatedFormData = {
                        ...prev,
                        departureAddress: fallbackAddress.address,
                        city: fallbackAddress.city,
                        postalCode: fallbackAddress.postcode,
                      };
                      console.log(
                        "📝 Géolocalisation: formData mis à jour avec fallback:",
                        updatedFormData,
                      );
                      return updatedFormData;
                    });

                    alert(
                      "Localisation réussie ! Coordonnées GPS récupérées (adresse approximative).",
                    );
                  }
                } catch (apiError) {
                  console.error("❌ Erreur API reverse geocoding:", apiError);

                  // Fallback en cas d'erreur API
                  const fallbackAddress = {
                    address: `Position GPS (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
                    postcode: "41000",
                    city: "Blois",
                  };

                  console.log("⚠️ Fallback après erreur API:", fallbackAddress);

                  setFormData((prev) => {
                    const updatedFormData = {
                      ...prev,
                      departureAddress: fallbackAddress.address,
                      city: fallbackAddress.city,
                      postalCode: fallbackAddress.postcode,
                    };
                    console.log(
                      "📝 Géolocalisation: formData mis à jour avec fallback après erreur:",
                      updatedFormData,
                    );
                    return updatedFormData;
                  });

                  alert(
                    "Localisation réussie ! Coordonnées GPS récupérées (adresse approximative).",
                  );
                }
              } catch (err) {
                if (err.code === 1) {
                  alert(
                    "Permission refusée. Veuillez autoriser la géolocalisation.",
                  );
                } else if (err.code === 2) {
                  alert("Position indisponible. Vérifiez votre connexion GPS.");
                } else if (err.code === 3) {
                  alert("Délai dépassé. Veuillez réessayer.");
                } else {
                  alert(`Erreur: ${err.message}`);
                }
              }
            }}
            disabled={submitting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 20px",
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: submitting ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
              marginTop: "8px",
            }}
          >
            📍 Se localiser
          </button>

          <small style={{ display: "block", marginTop: 6, color: "#6b7280" }}>
            🔒 Votre adresse n'est pas visible publiquement. Elle sert
            uniquement à estimer votre proximité avec les demandeurs pour
            prioriser l'appariement.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="availableSeats">Nombre de places disponibles :</label>
          <select
            id="availableSeats"
            name="availableSeats"
            value={formData.availableSeats}
            onChange={handleInputChange}
            required
          >
            <option value={1}>1 place</option>
            <option value={2}>2 places</option>
            <option value={3}>3 places</option>
            <option value={4}>4 places</option>
            <option value={5}>5 places</option>
          </select>
        </div>

        <div className="form-group">
          <label>Routine</label>
          <div
            className={`routine-option ${formData.routine ? "selected" : ""}`}
            onClick={() =>
              setFormData((prev) => ({ ...prev, routine: !prev.routine }))
            }
          >
            <span>
              🚀 Créer automatiquement des offres pour les 8 prochaines réunions
              (mercredi 19:30, samedi 18:00)
            </span>
          </div>
        </div>

        {!formData.routine && (
          <div className="form-group">
            <label>Dates proposées (cliquez sur une ou plusieurs) :</label>
            <div>
              {upcomingMeetings.map((m) => {
                const checked = formData.selectedMeetings.some(
                  (x) => x.meetingAt === m.meetingAt,
                );
                const pendingForIso = pendingCounts[m.isoDate] || 0;
                return (
                  <div
                    key={m.meetingAt}
                    className={`date-option ${checked ? "selected" : ""}`}
                    style={{ position: 'relative' }}
                    onClick={() => toggleMeetingSelection(m)}
                  >
                    <span>{m.label}</span>
                    {pendingForIso > 0 && (
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          fontSize: 10,
                          color: '#fff',
                          fontWeight: 800,
                          lineHeight: 1,
                          background: '#ef4444',
                          border: '2px solid #fff',
                          borderRadius: 999,
                          padding: '2px 6px',
                          boxShadow: '0 2px 6px rgba(0,0,0,.18)',
                          pointerEvents: 'none',
                        }}
                        title={`${pendingForIso} demande${pendingForIso>1?'s':''} en attente`}
                      >
                        {pendingForIso}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting
              ? "Création…"
              : formData.routine
                ? "Créer mes 8 prochaines offres"
                : "Créer mon offre"}
          </button>
        </div>
      </form>

      <div className="form-info">
        <p>
          🔁 Routine: permet de publier en un clic vos offres pour les 8
          prochaines réunions.
        </p>
      </div>
    </div>
  );
};

export default DriverForm;
